import React, { useState } from 'react';
import { Typography, Form, Input, Select, Button, message } from 'antd';
import {
  ArrowRightOutlined,
  CheckCircleFilled,
  UserOutlined,
  PhoneOutlined,
  BankOutlined, EnvironmentOutlined,
} from '@ant-design/icons';
import ScrollReveal from '../../components/ScrollReveal';
import ajax, { AjaxError } from '../../utils/ajax';
import contactHeroBg from '../../assets/images/contact-office-hero.png';
import contactMosaic from '../../assets/images/contact-mosaic-grid.png';
import ServiceNetwork from './components/ServiceNetwork';
import aboutMapBg from "../../assets/images/about/cut_04_compressed.webp";

const { Title } = Typography;
const { TextArea } = Input;

const needOptions = [
  { value: '产品功能与方案咨询', label: '产品功能与方案咨询' },
  { value: '云会计产品咨询', label: '云会计产品咨询' },
  { value: '进销存产品咨询', label: '进销存产品咨询' },
  { value: '行业解决方案', label: '行业解决方案' },
  { value: '实施与交付服务', label: '实施与交付服务' },
  { value: '售后支持', label: '售后支持' },
  { value: '商务合作', label: '商务合作' },
  { value: '其他留言', label: '其他留言' },

];

// 表单初始值:把「您的需求」预选到最常见的「申请产品使用」,减少一次点击
const initialValues = {
  need: '产品功能与方案咨询',
};

// 联系方式 / 隐私链接 / 顾问承诺 - 卡片底部「为什么放心提交」信任区
const trustPoints = [
  { icon: <CheckCircleFilled />, text: '24 小时内顾问主动回访' },
  { icon: <CheckCircleFilled />, text: '信息加密,仅用于本次需求沟通' },
  { icon: <CheckCircleFilled />, text: '免费试用,无任何强制消费' },
];

const Contact = () => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  // 监听「您的需求」选中值,用于判断是否展示额外的备注输入框
  const selectedNeed = Form.useWatch('need', form);
  const showOtherInput = selectedNeed === '其他留言';

  // 实时校验:onChange 即时反馈,但首次聚焦前不打扰用户
  const validateTrigger = 'onChange';

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      // 「其他留言」分支:把自由输入内容作为 requirement;否则直接传选中的需求
      const requirement =
        values.need === '其他留言' ? values.otherRequirement : values.need;
      const res = await ajax.post('/website/trial', {
        name: values.name,
        phone: values.phone,
        company: values.company,
        requirement,
      });
      // 后端约定 code !== 200 视为失败
      if (res && (res.code === 200 || res.code === 0)) {
        message.success('提交成功,顾问将在 24 小时内与您联系');
        form.resetFields(['name', 'phone', 'company', 'otherRequirement']);
      } else {
        throw new AjaxError(res?.msg || '提交失败,请稍后重试', res?.code);
      }
    } catch (err) {
      message.error(err.message || '提交失败,请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-contact">
      {/* Hero */}
      <section
        className="contact-hero"
        style={{ backgroundImage: `url(${contactHeroBg})` }}
      >
        <div className="contact-hero-inner">
          <ScrollReveal>
            <Title level={1} className="contact-hero-title">
              联系我们
            </Title>
          </ScrollReveal>
        </div>
      </section>

      {/* 主体区块标题:申请试用 */}
      <section className="contact-trial-head-section">
        <div className="contact-trial-head-inner">
          <ScrollReveal>
            <div className="contact-trial-head">
              <Title level={2} className="contact-trial-title">
                欢迎在线留言
              </Title>
              <span className="contact-trial-underline" />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* 主体:左 3x3 拼图 + 右表单卡片 */}
      <section className="contact-main-section">
        <div className="contact-main-inner">
          {/* 左:办公环境单图 */}
          <ScrollReveal className="contact-main-visual">
            <div
              className="contact-mosaic-image"
              style={{ backgroundImage: `url(${contactMosaic})` }}
            />
          </ScrollReveal>

          {/* 右:申请试用表单卡片 */}
          <ScrollReveal delay={150} className="contact-main-info">
            <div className="contact-card">
              {/* <span className="contact-card-eyebrow">申请试用</span> */}
              <Title level={3} className="contact-card-title">
                填写提交需求，顾问一对一专属跟进
              </Title>

              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                className="contact-form"
                initialValues={initialValues}
                requiredMark={false}
                validateTrigger={validateTrigger}
                scrollToFirstError
              >
                <div className="contact-form-row">
                  <Form.Item
                    name="name"
                    label={
                      <span className="contact-form-label">
                        称呼 <em className="required-star">*</em>
                      </span>
                    }
                    rules={[
                      { required: true, message: '请输入称呼' },
                      { whitespace: true, message: '称呼不能只输入空格' },
                      { max: 20, message: '称呼最多 20 个字符' },
                    ]}
                  >
                    <Input
                      placeholder="怎么称呼您"
                      size="large"
                      allowClear
                      prefix={<UserOutlined className="contact-input-icon" />}
                      maxLength={20}
                    />
                  </Form.Item>
                  <Form.Item
                    name="phone"
                    label={
                      <span className="contact-form-label">
                        联系电话 <em className="required-star">*</em>
                      </span>
                    }
                    rules={[
                      { required: true, message: '请输入联系电话' },
                      { pattern: /^1[3-9]\d{9}$/, message: '请输入有效手机号' },
                    ]}
                  >
                    <Input
                      placeholder="11 位手机号码"
                      size="large"
                      allowClear
                      prefix={<PhoneOutlined className="contact-input-icon" />}
                      maxLength={11}
                    />
                  </Form.Item>
                </div>

                <Form.Item
                  name="company"
                  label="企业名称"
                  rules={[{ max: 50, message: '企业名称最多 50 个字符' }]}
                >
                  <Input
                    placeholder="请填写企业名称，方便顾问了解您的业务情况"
                    size="large"
                    allowClear
                    prefix={<BankOutlined className="contact-input-icon" />}
                    maxLength={50}
                  />
                </Form.Item>

                <Form.Item name="need" label="您的需求">
                  <Select
                    size="large"
                    options={needOptions}
                    suffixIcon={<ArrowRightOutlined style={{ color: '#999' }} />}
                    popupMatchSelectWidth
                  />
                </Form.Item>

                {showOtherInput && (
                  <Form.Item
                    name="otherRequirement"
                    label="具体留言内容"
                    rules={[
                      { required: true, message: '请输入留言内容' },
                      { whitespace: true, message: '留言内容不能只输入空格' },
                      { max: 200, message: '留言内容最多 200 个字符' },
                    ]}
                  >
                    <Input.TextArea
                      placeholder="请简单描述您的需求或问题(200 字以内)"
                      size="large"
                      allowClear
                      maxLength={200}
                      autoSize={{ minRows: 3, maxRows: 6 }}
                    />
                  </Form.Item>
                )}

                <Form.Item className="contact-form-submit-wrap">
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={submitting}
                    className="contact-submit-btn"
                  >
                    {!submitting && '提交申请'}
                    {!submitting && <ArrowRightOutlined />}
                  </Button>
                </Form.Item>

                <p className="contact-form-tips">
                  表单提交成功后，我们将第一时间为您跟进，请您耐心等待回复！
                </p>
              </Form>
            </div>
          </ScrollReveal>
        </div>
      </section>
      {/* 公司地址 */}

      <section
          className="about-address-section"
          style={{  backgroundImage: `url(${aboutMapBg})` } }
      >
        <div className="about-address-overlay" />
        <div className="about-address-inner">
          <ScrollReveal>
            <div className="about-address-card">
              <div className="about-address-icon">
                <EnvironmentOutlined />
              </div>
              <div className="about-address-content">
                <h3 className="about-address-title">广州赤兴科技有限公司</h3>
                <p className="about-address-text">地址：广州市天河区棠东东路7号115房</p>
                <p className="about-address-zip">电话：18620706846</p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* 服务网络 — 中国地图可视化
      <ServiceNetwork />*/}
    </div>
  );
};

export default Contact;
